import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const formatINR = (n) => !isFinite(n) ? '₹0' : '₹' + Math.round(n).toLocaleString('en-IN');

const MortgageCalculator = () => {
  const [price, setPrice] = useState(8000000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(8.5);
  const [tenure, setTenure] = useState(20);
  const [propertyTax, setPropertyTax] = useState(15000);
  const [insurance, setInsurance] = useState(6000);

  const calc = useMemo(() => {
    const down = (price * downPct) / 100;
    const P = price - down;
    const r = rate / 12 / 100;
    const n = tenure * 12;
    const emi = P > 0 ? (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : 0;
    const monthlyTax = propertyTax / 12;
    const monthlyIns = insurance / 12;
    const totalMonthly = emi + monthlyTax + monthlyIns;
    return {
      down, loan: P, emi, monthlyTax, monthlyIns, totalMonthly,
      totalInterest: emi * n - P,
      totalPayment: emi * n + propertyTax * tenure + insurance * tenure,
    };
  }, [price, downPct, rate, tenure, propertyTax, insurance]);

  const schedule = useMemo(() => {
    const P = calc.loan;
    const r = rate / 12 / 100;
    const n = tenure * 12;
    let balance = P;
    const years = [];
    for (let y = 1; y <= tenure; y++) {
      let yearInterest = 0, yearPrincipal = 0;
      for (let m = 0; m < 12; m++) {
        const interest = balance * r;
        const principal = calc.emi - interest;
        yearInterest += interest; yearPrincipal += principal;
        balance -= principal;
      }
      years.push({ year: y, principal: yearPrincipal, interest: yearInterest, balance: Math.max(balance, 0) });
    }
    return years;
  }, [calc.loan, calc.emi, rate, tenure]);

  return (
    <CalculatorLayout
      title="Home Loan Calculator"
      subtitle="Full mortgage breakdown including property tax and insurance. Values in INR (₹)."
      category="Financial"
      description={<>
        <p>Calculate your total monthly home loan payment including principal, interest, property tax, and home insurance. This Indian-context calculator helps you plan home ownership with realistic costs.</p>
        <p>The amortization schedule shows year-by-year breakdown of principal and interest.</p>
      </>}
      formulaNote="Monthly Payment = EMI + (Annual Tax / 12) + (Annual Insurance / 12)"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          <Field label="Home Price" unit="₹" value={price} setValue={setPrice} min={500000} max={50000000} step={100000} fmt />
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Down Payment</Label>
              <div className="text-sm text-slate-600"><span className="font-semibold text-saffron-700">{formatINR(calc.down)}</span> <span className="text-slate-400">• {downPct}%</span></div>
            </div>
            <Slider min={0} max={80} step={1} value={[downPct]} onValueChange={(v) => setDownPct(v[0])} />
          </div>
          <Field label="Interest Rate" unit="%" value={rate} setValue={setRate} min={1} max={20} step={0.1} />
          <Field label="Tenure" unit="yr" value={tenure} setValue={setTenure} min={1} max={30} step={1} />
          <Field label="Property Tax (annual)" unit="₹" value={propertyTax} setValue={setPropertyTax} min={0} max={200000} step={1000} fmt />
          <Field label="Home Insurance (annual)" unit="₹" value={insurance} setValue={setInsurance} min={0} max={100000} step={500} fmt />
        </div>

        <div>
          <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Monthly Payment</div>
            <div className="font-display text-4xl font-bold text-bharat-navy display-font mt-1">{formatINR(calc.totalMonthly)}</div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
              <Stat label="Loan EMI" value={formatINR(calc.emi)} />
              <Stat label="Monthly Tax" value={formatINR(calc.monthlyTax)} />
              <Stat label="Monthly Insurance" value={formatINR(calc.monthlyIns)} />
              <Stat label="Loan Amount" value={formatINR(calc.loan)} />
              <Stat label="Total Interest" value={formatINR(calc.totalInterest)} highlight="saffron" />
              <Stat label="Total Cost" value={formatINR(calc.totalPayment + calc.down)} highlight="green" />
            </div>
          </div>

          <Tabs defaultValue="schedule" className="mt-4">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="schedule">Amortization</TabsTrigger>
              <TabsTrigger value="summary">Summary</TabsTrigger>
            </TabsList>
            <TabsContent value="schedule">
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-slate-600">
                        <th className="px-3 py-2 text-left font-semibold">Yr</th>
                        <th className="px-3 py-2 text-right font-semibold">Principal</th>
                        <th className="px-3 py-2 text-right font-semibold">Interest</th>
                        <th className="px-3 py-2 text-right font-semibold">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.map(s => (
                        <tr key={s.year} className="border-t border-slate-100 display-font">
                          <td className="px-3 py-1.5 text-slate-500">{s.year}</td>
                          <td className="px-3 py-1.5 text-right text-indgreen-700">{formatINR(s.principal)}</td>
                          <td className="px-3 py-1.5 text-right text-saffron-700">{formatINR(s.interest)}</td>
                          <td className="px-3 py-1.5 text-right text-bharat-navy font-medium">{formatINR(s.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="summary">
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-700 space-y-2">
                <p>You'll pay <span className="font-semibold text-bharat-navy">{formatINR(calc.totalMonthly)}</span> every month for <span className="font-semibold">{tenure} years</span>.</p>
                <p>Total interest outgo: <span className="font-semibold text-saffron-700">{formatINR(calc.totalInterest)}</span> — that's <span className="font-semibold">{((calc.totalInterest/calc.loan)*100).toFixed(0)}%</span> of your loan amount.</p>
                <p>Including down payment, tax & insurance, your total home cost: <span className="font-semibold text-indgreen-700">{formatINR(calc.totalPayment + calc.down)}</span>.</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Field = ({ label, unit, value, setValue, min, max, step, fmt }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="flex items-center gap-1 bg-slate-100 rounded-md px-2 py-1">
        {unit === '₹' && <span className="text-slate-700 font-semibold text-sm">₹</span>}
        <Input type="number" step={step} value={value} onChange={(e) => setValue(Math.max(min, +e.target.value))} className="w-24 h-7 border-0 bg-transparent p-0 text-right font-semibold focus-visible:ring-0" />
        {unit !== '₹' && <span className="text-slate-700 font-semibold text-sm">{unit}</span>}
      </div>
    </div>
    <Slider min={min} max={max} step={step} value={[value]} onValueChange={(v) => setValue(v[0])} />
    <div className="flex justify-between text-[10px] text-slate-400 mt-1">
      <span>{fmt ? formatINR(min) : min + (unit !== '₹' ? unit : '')}</span>
      <span>{fmt ? formatINR(max) : max + (unit !== '₹' ? unit : '')}</span>
    </div>
  </div>
);

const Stat = ({ label, value, highlight }) => {
  const color = highlight === 'saffron' ? 'text-saffron-700' : highlight === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="bg-white rounded-md p-2.5 border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`display-font font-semibold mt-0.5 ${color}`}>{value}</div>
    </div>
  );
};

export default MortgageCalculator;
